import { type PolymorphicProps, useSlotProps } from '@mui/base'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type ProductBadgeOwnProps<RootComponentType extends ElementType = 'span'> = {
  ref?: ComponentProps<RootComponentType>['ref']

  slotProps?: {
    root?: ComponentProps<RootComponentType>
  }

  slots?: {
    root?: RootComponentType
  }
}

interface ProductBadgeTypeMap<RootComponentType extends ElementType = 'span'> {
  defaultComponent: RootComponentType
  props: ProductBadgeOwnProps<RootComponentType>
}

type ProductBadgeProps<
  RootComponentType extends ElementType =
    ProductBadgeTypeMap['defaultComponent'],
> = PolymorphicProps<ProductBadgeTypeMap<RootComponentType>, RootComponentType>

export const ProductBadge = <RootComponentType extends ElementType = 'span'>({
  ref,
  slotProps,
  slots,
  ...other
}: ProductBadgeProps<RootComponentType>): JSX.Element => {
  const Root = slots?.root ?? 'span'
  const rootProps = useSlotProps({
    additionalProps: {
      ref,
    },
    className: 'product-badge',
    elementType: Root,
    externalForwardedProps: other,
    externalSlotProps: slotProps?.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}
